import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faTimes, 
  faMinus, 
  faPlus, 
  faTrash, 
  faTruck, 
  faShoppingBag,
  faCheck
} from '@fortawesome/free-solid-svg-icons';
import { Link } from 'react-router-dom';
import parameterService from '../../services/parameterService';
import CampaignCalculator from '../CampaignCalculator';

const ShoppingCartSidebar = ({ isOpen, onClose, cartItems, onUpdateQuantity, onRemoveItem, onViewCart, onCheckout }) => {
  const [shippingParams, setShippingParams] = useState({
    shippingFee: 0,
    freeShippingThreshold: 300,
    shippingEnabled: true
  });
  
  // Kampanya state'i
  const [campaignData, setCampaignData] = useState(null);

  // Kargo parametrelerini yükle
  useEffect(() => {
    const loadShippingParameters = async () => {
      try {
        const fee = await parameterService.getParameter('shipping_fee', 0);
        const threshold = await parameterService.getParameter('free_shipping_threshold', 300);
        const enabled = await parameterService.getParameter('shipping_enabled', true);
        
        setShippingParams({
          shippingFee: fee || 0,
          freeShippingThreshold: threshold || 300,
          shippingEnabled: enabled !== false
        });
      } catch (error) {
        console.error('Kargo parametreleri yüklenirken hata:', error);
      }
    };

    loadShippingParameters();
  }, []);

  // Toplam fiyat hesaplama
  const calculateSubtotal = () => {
    return cartItems.reduce((total, item) => total + (item.price * item.quantity), 0);
  };

  const subtotal = calculateSubtotal();
  const remainingForFreeShipping = Math.max(0, shippingParams.freeShippingThreshold - subtotal);
  const hasFreeShipping = subtotal >= shippingParams.freeShippingThreshold;
  const progressPercentage = Math.min(100, (subtotal / shippingParams.freeShippingThreshold) * 100);

  // Kargo ücreti hesaplama
  const getShippingFee = () => {
    if (!shippingParams.shippingEnabled) {
      return 0; // Kargo pasifse ücretsiz
    }
    return subtotal >= shippingParams.freeShippingThreshold ? 0 : shippingParams.shippingFee;
  };

  const shippingFee = getShippingFee();
  
  // Kampanya indirimi hesapla
  const campaignDiscount = campaignData && campaignData.campaignActive ? campaignData.savings : 0;
  
  const total = subtotal + shippingFee - campaignDiscount;

  // Sidebar animasyon varyantları
  const sidebarVariants = {
    closed: {
      x: '100%',
      transition: {
        type: 'spring',
        stiffness: 300,
        damping: 30
      }
    },
    open: {
      x: 0,
      transition: {
        type: 'spring',
        stiffness: 300,
        damping: 30
      }
    }
  };

  const overlayVariants = {
    closed: {
      opacity: 0,
      transition: {
        duration: 0.2
      }
    },
    open: {
      opacity: 1,
      transition: {
        duration: 0.2
      }
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
                                {/* Overlay */}
           <motion.div
             className="fixed inset-0 bg-black/60 z-[9998]"
             variants={overlayVariants}
             initial="closed"
             animate="open"
             exit="closed"
             onClick={onClose}
             style={{ zIndex: 9998 }}
           />

                     {/* Sidebar */}
          <motion.div
            className="fixed top-0 right-0 h-full w-80 sm:w-96 bg-white shadow-xl flex flex-col border-l border-gray-200"
            variants={sidebarVariants}
            initial="closed"
            animate="open"
            exit="closed"
            style={{ zIndex: 9999 }}
          >
            {/* Header */}
            <div className="flex items-center justify-between p-4 sm:p-6 border-b border-gray-100">
              <h2 className="text-lg sm:text-xl font-bold text-gray-900">Sepetim</h2>
              <button
                onClick={onClose}
                className="p-1.5 sm:p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-all duration-200 hover:scale-105 active:scale-95"
              >
                <FontAwesomeIcon icon={faTimes} className="text-base sm:text-lg" />
              </button>
            </div>

                                                   {/* Free Shipping Progress */}
              {shippingParams.shippingEnabled && !hasFreeShipping && remainingForFreeShipping > 0 && (
                <div className="p-3 sm:p-4 bg-gradient-to-r from-blue-50 to-indigo-50 border-b border-gray-100">
                  <div className="flex items-center gap-2 mb-2">
                    <FontAwesomeIcon icon={faTruck} className="text-blue-600 text-xs sm:text-sm" />
                    <span className="text-xs sm:text-sm font-medium text-blue-800">
                      {remainingForFreeShipping.toFixed(2)} TL daha ekleyin, kargo bedava!
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden relative">
                    <div 
                      className="bg-gradient-to-r from-blue-500 to-indigo-600 h-2 rounded-full relative transition-all duration-300"
                      style={{ width: `${progressPercentage}%` }}
                    >
                      {/* Animasyonlu çizgiler */}
                      <div 
                        className="absolute inset-0 opacity-20"
                        style={{
                          backgroundImage: `repeating-linear-gradient(
                            45deg,
                            transparent,
                            transparent 3px,
                            rgba(255, 255, 255, 0.3) 3px,
                            rgba(255, 255, 255, 0.3) 6px
                          )`,
                          animation: 'progressStripes 1.5s linear infinite'
                        }}
                      />
                    </div>
                    
                    
                  </div>
                </div>
              )}

                                                   {/* Free Shipping Congratulations */}
              {shippingParams.shippingEnabled && hasFreeShipping && (
                <div className="p-3 sm:p-4 bg-gradient-to-r from-green-50 to-emerald-50 border-b border-gray-100">
                  <div className="flex items-center gap-2 sm:gap-3 mb-2 sm:mb-3">
                    <div className="w-8 h-8 sm:w-10 sm:h-10 bg-gradient-to-r from-green-400 to-emerald-500 rounded-full flex items-center justify-center shadow-md">
                      <FontAwesomeIcon icon={faCheck} className="text-white text-sm sm:text-lg" />
                    </div>
                    <div>
                      <h3 className="font-bold text-green-800 text-sm sm:text-base">Tebrikler! Ücretsiz kargo kazandınız!</h3>
                      <p className="text-xs sm:text-sm font-medium text-green-700">Siparişiniz ücretsiz kargo ile gönderilecek</p>
                    </div>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden relative">
                    <div 
                      className="bg-gradient-to-r from-green-500 to-emerald-600 h-2 rounded-full relative"
                      style={{ width: '100%' }}
                    >
                      {/* Animasyonlu çizgiler */}
                      <div 
                        className="absolute inset-0 opacity-30"
                        style={{
                          backgroundImage: `repeating-linear-gradient(
                            45deg,
                            transparent,
                            transparent 4px,
                            rgba(255, 255, 255, 0.4) 4px,
                            rgba(255, 255, 255, 0.4) 8px
                          )`,
                          animation: 'progressStripes 2s linear infinite'
                        }}
                      />
                    </div>
                  </div>
                </div>
              )}

            {/* Cart Items */}
            <div className="flex-1 overflow-y-auto p-3 sm:p-4 space-y-3 sm:space-y-4">
              {cartItems.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-full text-center">
                  <FontAwesomeIcon icon={faShoppingBag} className="text-4xl text-gray-300 mb-4" />
                  <p className="text-gray-500">Sepetiniz boş</p>
                </div>
              ) : (
                cartItems.map((item) => (
                  <div key={`${item.id}-${item.variant}`} className="bg-white rounded-xl shadow-md p-3 sm:p-4 border border-gray-100 hover:shadow-lg transition-all duration-300">
                    <div className="flex gap-3 sm:gap-4">
                      {/* Product Image */}
                      <div className="flex-shrink-0">
                        {item.image ? (
                                                  <img
                          src={item.image}
                          alt={item.name || 'Ürün'}
                          className="w-24 h-24 sm:w-32 sm:h-32 object-cover rounded-lg bg-gray-100"
                          onError={(e) => {
                            e.target.style.display = 'none';
                            e.target.nextSibling.style.display = 'flex';
                          }}
                        />
                        ) : null}
                        <div 
                          className={`w-24 h-24 sm:w-32 sm:h-32 rounded-lg bg-gray-200 flex items-center justify-center ${item.image ? 'hidden' : 'flex'}`}
                        >
                          <FontAwesomeIcon icon={faShoppingBag} className="text-gray-400 text-sm sm:text-lg" />
                        </div>
                      </div>

                      {/* Product Details */}
                      <div className="flex-1 min-w-0">
                        <h3 className="font-semibold text-gray-900 truncate text-sm sm:text-base">{item.name}</h3>
                        <div className="flex items-center gap-2 mt-1">
                          <p className="text-xs sm:text-sm text-gray-600 font-medium">{item.variant}</p>
                        </div>
                        <p className="text-base sm:text-lg font-bold text-gray-900 mt-1">
                          {item.price.toFixed(2)} TL
                        </p>

                        {/* Quantity Controls */}
                        <div className="flex items-center justify-between mt-2 sm:mt-3">
                          <div className="flex items-center gap-1 sm:gap-2">
                            <button
                              onClick={() => onUpdateQuantity(item.id, item.variant, Math.max(1, item.quantity - 1))}
                              className="w-7 h-7 sm:w-8 sm:h-8 rounded-lg bg-gray-100 hover:bg-gray-200 flex items-center justify-center transition-all duration-200 hover:scale-105 active:scale-95"
                            >
                              <FontAwesomeIcon icon={faMinus} className="text-gray-600 text-xs" />
                            </button>
                            <span className="w-7 sm:w-8 text-center font-semibold text-gray-800 text-sm">{item.quantity}</span>
                            <button
                              onClick={() => onUpdateQuantity(item.id, item.variant, item.quantity + 1)}
                              className="w-7 h-7 sm:w-8 sm:h-8 rounded-lg bg-gray-100 hover:bg-gray-200 flex items-center justify-center transition-all duration-200 hover:scale-105 active:scale-95"
                            >
                              <FontAwesomeIcon icon={faPlus} className="text-gray-600 text-xs" />
                            </button>
                          </div>

                          {/* Remove Button */}
                          <button
                            onClick={() => onRemoveItem(item.id, item.variant)}
                            className="p-1.5 sm:p-2 text-red-500 hover:bg-red-50 rounded-lg transition-all duration-200 hover:scale-105 active:scale-95"
                          >
                            <FontAwesomeIcon icon={faTrash} className="text-xs sm:text-sm" />
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>

            {/* Footer */}
            <div className="border-t border-gray-100 p-4 sm:p-6 space-y-3 sm:space-y-4">
              {/* Subtotal */}
              <div className="flex justify-between items-center">
                <span className="text-xs sm:text-sm font-medium text-gray-600">Ara Toplam</span>
                <span className="text-base sm:text-lg font-semibold text-gray-900">
                  {subtotal.toFixed(2)} TL
                </span>
              </div>
              
              {/* Kampanya Bilgisi */}
              <CampaignCalculator 
                cartItems={cartItems}
                onCampaignCalculated={setCampaignData}
                showInSummary={true}
              />
              
              {/* Shipping */}
              {shippingParams.shippingEnabled && (
                <div className="flex justify-between items-center">
                  <span className="text-xs sm:text-sm font-medium text-gray-600">Kargo</span>
                  <span className="text-base sm:text-lg font-semibold text-gray-900">
                    {shippingFee === 0 ? 'Ücretsiz' : `${shippingFee.toFixed(2)} TL`}
                  </span>
                </div>
              )}
              
              {/* Kampanya İndirimi */}
              {campaignData && campaignData.campaignActive && campaignData.savings > 0 && (
                <div className="flex justify-between items-center text-green-600">
                  <span className="text-xs sm:text-sm font-medium">Kampanya İndirimi</span>
                  <span className="text-base sm:text-lg font-semibold">
                    -{campaignData.savings.toFixed(2)} TL
                  </span>
                </div>
              )}
              
              {/* Total */}
              <div className="flex justify-between items-center pt-2 border-t border-gray-100">
                <span className="text-base sm:text-lg font-semibold text-gray-800">Toplam</span>
                <span className="text-xl sm:text-2xl font-bold text-gray-900">
                  {total.toFixed(2)} TL
                </span>
              </div>

              {/* Action Buttons */}
              <div className="space-y-2 sm:space-y-3">
                <button
                  onClick={onViewCart}
                  className="w-full py-2.5 sm:py-3 px-4 sm:px-6 bg-white border-2 border-gray-300 text-gray-700 font-semibold rounded-lg hover:bg-gray-50 hover:border-gray-400 transition-all duration-300 hover:shadow-md active:scale-95 text-sm sm:text-base"
                >
                  Sepeti Görüntüle
                </button>
                <button
                  onClick={onCheckout}
                  disabled={cartItems.length === 0}
                  className="w-full py-2.5 sm:py-3 px-4 sm:px-6 bg-gradient-to-r from-amber-600 to-amber-700 text-white font-semibold rounded-lg hover:from-amber-700 hover:to-amber-800 transition-all duration-300 hover:shadow-lg hover:-translate-y-0.5 active:translate-y-0 disabled:from-gray-300 disabled:to-gray-400 disabled:cursor-not-allowed disabled:transform-none disabled:shadow-none text-sm sm:text-base"
                >
                  Ödeme Yap
                </button>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default ShoppingCartSidebar;
